import Core from '@adyen/adyen-web/dist/types/core';

export type PaymentMethodType =
  | 'scheme'
  | 'klarna'
  | 'klarna_account'
  | 'klarna_paynow'
  | 'paysafecard'
  | 'swish'
  | 'trustly'
  | 'vipps'
  | 'multibanco'
  | 'ideal'
  | 'paypal'
  | 'clearpay'
  | 'applepay';

export interface PaymentMethod {
  name: string;
  type: PaymentMethodType;
  image: {
    src: string;
  };
  configuration?: {
    merchantId: string;
    merchantName: string;
    domainName: string;
  };
}

export interface ApplePaySessionRequestPayload {
  domainName: string;
  merchantIdentifier: string;
  displayName: string;
}

export interface RedirectAction {
  type: 'redirect';
  method: string;
  data?: unknown;
  url: string;
  paymentMethodType: string;
}

export interface ThreeDS2Action {
  type: 'threeDS2';
  authorisationToken: string;
  paymentData: string;
  paymentMethodType: string;
  subtype: string;
  token: string;
  url: string;
}
export type PaymentAction = RedirectAction | ThreeDS2Action;

export type PaymentResponse = {
  additionalData: Record<string, string>;
  pspReference: string;
  resultCode:
    | 'Authorised'
    | 'Cancelled'
    | 'Error'
    | 'Refused'
    | 'RedirectShopper'
    | 'IdentifyShopper'
    | 'ChallengeShopper';
  merchantReference: string;
  action?: PaymentAction;
};

export type ApplePaySessionResponse = {
  data: string;
};

export interface SchemePaymentRequestPayload {
  amount: {
    currency: string;
    value: number;
  };
  reference: string;
  paymentMethod: SchemeData;
  returnUrl: string;
  channel: 'web';
  origin: string;
  countryCode: string;
  shopperLocale: string;
  browserInfo: {
    acceptHeader: string;
    colorDepth: number;
    javaEnabled: boolean;
    javaScriptEnabled?: boolean;
    language: string;
    screenHeight: number;
    screenWidth: number;
    timeZoneOffset: number;
    userAgent: string;
  };
  authenticationData: {
    threeDSRequestData: {
      nativeThreeDS: 'preferred';
    };
  };
  metadata?: Record<string, unknown>;
}

export interface KlarnaLineItem {
  id: string;
  quantity: string;
  description: string;
  amountIncludingTax: number | string;
  productUrl?: string;
  imageUrl?: string;
}

export interface LineItem {
  id: string;
  quantity: string;
  description: string;
  amountIncludingTax: number | string;
  productUrl?: string;
  imageUrl?: string;
}

export interface KlarnaPaymentRequestPayload {
  amount: {
    currency: string;
    value: number;
  };
  reference: string;
  paymentMethod: Pick<KlarnaData, 'type'>;
  returnUrl: string;
  countryCode: string;
  shopperLocale: string;
  shopperReference: string;
  shopperEmail: string;
  shopperName?: {
    firstName: string;
    lastName?: string;
  };
  lineItems: Array<KlarnaLineItem>;
  metadata?: Record<string, unknown>;
}

export interface IdealPaymentRequestPayload {
  amount: {
    currency: string;
    value: number;
  };
  reference: string;
  paymentMethod: Pick<IdealData, 'type' | 'issuer'>;
  returnUrl: string;
  countryCode: string;
  shopperLocale: string;
  shopperReference: string;
  lineItems: Array<LineItem>;
  metadata?: Record<string, unknown>;
}

export interface ClearpayPaymentRequestPayload {
  amount: {
    currency: string;
    value: number;
  };
  reference: string;
  paymentMethod: Pick<clearpayData, 'type'>;
  returnUrl: string;
  countryCode: string;
  shopperLocale: string;
  shopperReference: string;
  shopperEmail: string;
  shopperName?: {
    firstName: string;
    lastName?: string;
  };
  lineItems: Array<LineItem>;
  metadata?: Record<string, unknown>;
}

export interface PayPalPaymentRequestPayload {
  amount: {
    currency: string;
    value: number;
  };
  reference: string;
  paymentMethod: Pick<PayPalData, 'type'>;
  returnUrl: string;
  countryCode: string;
  shopperLocale: string;
  shopperReference: string;
  lineItems: Array<LineItem>;
  metadata?: Record<string, unknown>;
}

export interface ApplePayPaymentRequestPayload {
  amount: {
    currency: string;
    value: number;
  };
  reference: string;
  paymentMethod: Pick<ApplePayData, 'type' | 'applePayToken'>;
  returnUrl: string;
  countryCode: string;
  shopperLocale: string;
  shopperReference: string;
  lineItems: Array<LineItem>;
  metadata?: Record<string, unknown>;
}

export type PaymentRequestPayload =
  | SchemePaymentRequestPayload
  | KlarnaPaymentRequestPayload
  | PayPalPaymentRequestPayload
  | IdealPaymentRequestPayload
  | ClearpayPaymentRequestPayload
  | ApplePayPaymentRequestPayload;

export interface SchemeData {
  type: 'scheme';
  brand: string;
  holderName: string;
  number: string;
  expiryMonth: string;
  expiryYear: string;
  cvc: string;
}

export interface KlarnaData {
  type: 'klarna_paynow';
  shopperEmail: string;
  shopperFirstName: string;
  shopperLastName?: string;
}

export interface clearpayData {
  type: 'clearpay';
}

export interface IdealData {
  type: 'ideal';
  issuer: string;
}

export interface PayPalData {
  type: 'paypal';
}

export interface ApplePayData {
  type: 'applepay';
  applePayToken: string;
}

export interface PaymentState {
  isValid: boolean;
  data: {
    paymentMethod: {
      type: string;
      holderName: string;
      brand: string;
    };
  };
}

export interface V12PaymentData {
  deposit: number;
  productGuid: string;
  productId: string;
  productName?: string;
}

export enum PaymentType {
  V12 = 'v12',
}

export type PaymentData = SchemeData | KlarnaData | IdealData | PayPalData | ApplePayData;

export type ThreeDS2AuthCallback = (response: PaymentResponse) => void;

export interface PaymentProvider {
  processing: boolean;
  setProcessing: (val: boolean) => void;
  adyenCheckoutInstance: Core | null;
  setAdyenCheckoutInstance: (data: Core | null) => void;
  adyenCheckoutInstanceState: PaymentState | null;
  setAdyenCheckoutInstanceState: (data: PaymentState | null) => void;
  selectedPaymentMethod: string | null;
  setSelectedPaymentMethod: (data: string | null) => void;
  paymentStatus: string;
  setPaymentStatus: (data: string) => void;
  setIsOverlayActive: (data: boolean) => void;
  isOverlayActive: boolean;
  isPaymentDataValid: boolean;
  setIsPaymentDataValid: (data: boolean) => void;
  v12PaymentData: V12PaymentData | null;
  setV12PaymentData: (data: V12PaymentData | null) => void;
  setMountedComponent: (data: any) => void;
  mountedComponent: any;
  paymentSession: any;
  setPaymentSession: (data: any) => void;
}
